import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui';

type Mode = 'password' | 'magic';

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(username, password);
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connection failed.');
      setBusy(false);
    }
  }

  async function submitMagic(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    setDevLink(null);
    try {
      const res = await api.requestMagicLink(email);
      setNotice('If an account exists for this address, a login link has been sent. Check your inbox.');
      if (res.devLink) setDevLink(res.devLink); // no mail key configured — show the link directly
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connection failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-white px-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-2xl bg-white p-10 text-gray-900 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
          <div className="mb-6 flex flex-col items-center gap-1">
            <img src="/ecomstat-logo.png" alt="ecomstat.io" className="h-9 w-auto" />
            <span className="text-xs font-medium uppercase tracking-widest text-gray-400">Digital Asset Manager</span>
          </div>

          {mode === 'password' ? (
            <form onSubmit={submitPassword} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">Username</span>
                <input value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus autoComplete="username"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">Password</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              <Button type="submit" variant="primary" loading={busy} className="w-full">Sign in</Button>
            </form>
          ) : (
            <form onSubmit={submitMagic} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">Email address</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="email"
                  placeholder="you@ecomstat.io"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-[13px] text-green-800" role="status">{notice}</p>}
              {devLink && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800 break-all">
                  No email delivery configured — direct link:{' '}
                  <a href={devLink} className="font-medium underline">{devLink}</a>
                </p>
              )}
              <Button type="submit" variant="primary" loading={busy} className="w-full">Send login link</Button>
            </form>
          )}

          <button
            type="button"
            onClick={() => { setMode(mode === 'password' ? 'magic' : 'password'); setError(null); setNotice(null); setDevLink(null); }}
            className="mt-4 w-full text-center text-[13px] font-medium text-brand-600 hover:text-brand-700"
          >
            {mode === 'password' ? 'Sign in with an email link instead' : 'Sign in with a password instead'}
          </button>
        </div>
      </div>
    </div>
  );
}
